/**
 * The Overview screen's reads.
 *
 *   fetchOverview          GET /overview            every tile on the dashboard, in one read
 *   fetchOverviewActivity  GET /overview/activity   the daily bars, and what has stopped itself
 *   fetchEngagement        GET /overview/engagement opens, clicks, and the per-ISP panel
 *   fetchOpportunities     GET /overview/opportunities  what is worth doing next
 *   saveOpportunityCohort  POST /overview/opportunities/:kind/cohort  that count, as a segment
 *   fetchCalendar          GET /overview/calendar   the dates worth campaigning on
 *
 * **One request for the dashboard, deliberately, and that is the interesting
 * decision here.** Every number that screen shows already has a route:
 * `/campaigns/:id/results` has the delivery counts, `/credits/balances` has the
 * balances, `/integrations` has the stored SES and Meta checks, `/campaigns` has
 * the recent list. The Overview could have been assembled in the browser out of
 * four or five of them and it would have needed no new API code at all. It is
 * not, for two reasons:
 *
 *  1. **A dashboard assembled from five responses is five moments, not one.**
 *     Each call is served by its own request, each opens its own RLS-bound
 *     transaction, and the screen would then invite a reader to compare a bounce
 *     count taken at 10:04:01 with an accepted count taken at 10:04:03. The
 *     composite response carries `generatedAt` because the whole page is one
 *     snapshot, and only a single read can honestly claim that.
 *  2. **None of those routes aggregates across campaigns.** The workspace bounce
 *     rate is not the average of per-campaign bounce rates — it is one ratio
 *     over one pair of sums — and a client that averaged five percentages would
 *     produce a plausible number that is arithmetically wrong. The sum belongs
 *     where the rows are.
 *
 * **`fetchOverview` reaches no provider, and it could not.** Integration health
 * on the Overview is rendered from the `last_check` snapshot stored on the
 * integrations row. That is why `overviewSesSendability` and its WhatsApp
 * counterpart both carry an `overviewSnapshot` — `checkedAt`, `ageMs`, `stale` —
 * and why the screen says how old the facts are instead of implying they are
 * current. A live `GetAccount` per dashboard load would put an AWS API call on
 * the critical path of the first screen after sign-in, and it would bill the
 * tenant's own account for the privilege of drawing a page.
 *
 * **Which is exactly why engagement is a second request rather than four more
 * fields on the first one.** Slice 11 adds opens, clicks and a per-ISP
 * deliverability breakdown, and the last of those is not a stored fact: the ISP
 * rows come from a live SESv2 `BatchGetMetricData` against the tenant's own AWS
 * account, because that dimension exists nowhere else — the SNS event stream
 * knows every bounce with its recipient and does not know which provider it came
 * from. Folding it into `GET /overview` would have repealed the paragraph above
 * in one commit: drawing the dashboard would spend a tenant's provider rate
 * limit, and an AWS timeout would take down the screen that reports bounce and
 * complaint rates — the two numbers that can cost somebody their AWS account.
 * So the split is not tidiness. It is what keeps the first screen after sign-in
 * independent of whether Amazon is answering right now.
 *
 * The two reads are therefore **two moments**, and that is the price paid for
 * it. It is affordable here and would not have been above: nothing on the
 * engagement response is compared arithmetically with anything on the dashboard
 * response, because an engagement rate's denominator is *delivered* email inside
 * the engagement response itself. Both carry their own timestamp so the screen
 * never implies a shared one.
 *
 * **Slice 20 adds a third read for the same reason there was a second one, and
 * not for the same reason there was a first.** The dashboard's charts are one
 * entry per UTC day per channel over thirty days — thirty times the row count of
 * `GET /overview`, grouped over a monthly-partitioned table — and they are the
 * part of the screen that can afford to arrive second. Folding them into the
 * composite response would have made the tiles wait on the bars; folding them
 * into `overviewResponseSchema` would have repealed that schema's stated
 * property in one line, which its own header says at the place somebody would
 * try it. So the tiles render from the first read and the charts fill in beneath
 * them, and neither can fail the other.
 *
 * The activity read reaches **no provider either**. Every count in it comes from
 * `messages` and `delivery_events` in this database, which is why it carries no
 * snapshot age: there is no stored third-party fact in it to have gone stale.
 * That makes it **five moments rather than two** — opportunities and the
 * calendar are the fourth and fifth, each its own read for the reason the third
 * was. The price is affordable for the reason it was above, with one invariant
 * deliberately kept across two of the five, and it is worth the assertion: the
 * daily `accepted` counts
 * on the email series sum to `sending.accepted` on the composite response,
 * because both are computed from the same constant over the same predicate. A
 * chart whose bars do not add up to the tile above them teaches the reader that
 * one of the two is approximate, and neither is.
 *
 * Both responses are parsed against their contract exactly as `campaigns.ts` and
 * `credits.ts` parse theirs, so a contract drift shows up here rather than as
 * `undefined` in a tile. Both schemas are `.strict()`, and on
 * `overviewResponseSchema` that is still doing the work slice 10 asked of it:
 * engagement now exists, and it still may not appear on `GET /overview`. An open
 * rate added by a mapper on the API side would fail the parse here rather than
 * arriving on the dashboard beside the exact rates, which is precisely the
 * confusion the separate endpoint exists to prevent.
 */
import {
  calendarResponseSchema,
  engagementResponseSchema,
  opportunitiesResponseSchema,
  overviewActivityResponseSchema,
  overviewResponseSchema,
  type CalendarResponse,
  type EngagementResponse,
  type OpportunitiesResponse,
  type OverviewActivityResponse,
  type OverviewResponse,
  addOwnMomentRequestSchema,
  reachLedgerSchema,
  type ReachLedger,
  okResponseSchema,
  ownMomentResponseSchema,
  type AddOwnMomentRequest,
  type OwnMoment,
  saveCohortRequestSchema,
  saveCohortResponseSchema,
  type OpportunityKind,
  type SaveCohortResponse,
} from '@wend/shared';

import { apiRequest } from './client';

/**
 * The Overview screen's single read.
 *
 * There is no query, no paging and no filter, and none belongs here. The window
 * is fixed by `OVERVIEW_ACTIVITY_WINDOW_DAYS` in the contract and echoed back on
 * the response as `window`, so the screen prints the stretch of time the API
 * actually summed rather than the one a component believed it asked for. A
 * date-range picker is a report, and a report is a different screen.
 */
export async function fetchOverview(signal?: AbortSignal): Promise<OverviewResponse> {
  return apiRequest({
    path: '/overview',
    schema: overviewResponseSchema,
    signal,
  });
}

/**
 * Opens, clicks, and the per-ISP breakdown — the one read on this screen that
 * can reach Amazon.
 *
 * **Called separately from {@link fetchOverview} and never awaited alongside
 * it.** The caller runs it through its own `useAsyncData`, so this request's
 * loading and failure states belong to one card and the rest of the page renders
 * regardless. A tenant whose VDM lookup is timing out still sees their bounce
 * rate; a tenant who has never switched tracking on pays nothing for this beyond
 * one cheap round trip that answers "off".
 *
 * Two failures are different and only one of them arrives here as a rejection.
 * A VDM lookup that fails comes back **inside** a successful response, as
 * `isp: { available: false, reason: 'lookup_failed' }` with the sentence to
 * print — because a deliverability panel Amazon would not answer for is not an
 * error in Wend, and rendering it as one would tell a tenant their mail is
 * broken when their metrics API is merely slow. A rejection from this function
 * means the request itself failed: no engagement figures at all, and the card
 * says so with a retry.
 *
 * The response carries the tenant's tracking settings alongside the numbers, so
 * the screen can say *which* switch is off and what turning it on would do
 * without a second request and without the frontend keeping its own copy of a
 * setting that lives in the database.
 */
export async function fetchEngagement(signal?: AbortSignal): Promise<EngagementResponse> {
  return apiRequest({
    path: '/overview/engagement',
    schema: engagementResponseSchema,
    signal,
  });
}

/**
 * The daily series behind the charts, and the campaigns that have stopped
 * themselves.
 *
 * **No query parameters, deliberately.** The window is the Overview's own —
 * `OVERVIEW_ACTIVITY_WINDOW_DAYS`, resolved by the same arithmetic — and it is
 * echoed back on the response as `window` alongside a `bucketing` block naming
 * the unit, the time zone and the column the buckets were cut on. A screen that
 * asked for a range would be a report, and a report is `/analytics`.
 *
 * Two things travel on one response rather than two, and it is not laziness.
 * `attention` — the halted and credit-paused campaigns — is read at the same
 * instant as the charts because it is the most urgent thing this screen can say
 * and it must not be inferred from the five-row recent-campaigns list, where a
 * campaign halted three weeks ago has already fallen off. It is small, it is
 * cheap, and it comes from the same tables.
 *
 * A rejection here blanks the charts and the attention block together, and
 * leaves every tile above them untouched. That is the right trade: a stopped
 * campaign that cannot be read about is a screen that says nothing, which is bad
 * — a bounce rate that cannot be read is a screen that says nothing about the
 * thing that can cost somebody their AWS account, which is worse.
 */
export async function fetchOverviewActivity(
  signal?: AbortSignal,
): Promise<OverviewActivityResponse> {
  return apiRequest({
    path: '/overview/activity',
    schema: overviewActivityResponseSchema,
    signal,
  });
}

/**
 * The four things worth doing next, counted from the workspace's own rows.
 *
 * **Its own request, for the reason the other two are their own requests and one
 * more.** The counts behind it scan `contacts` against `messages` twice with a
 * `NOT EXISTS` on each side — the heaviest read this screen issues on a large
 * list — and they are issued for the panel that suggests what to do rather than
 * the one that says what went wrong. A suggestion must never be able to slow
 * down, or fail, the bounce rate.
 *
 * **An empty array is a success and the card must render nothing for it.** A
 * workspace with nobody dormant, nobody unmessaged, every template used and
 * every segment sent to is not a workspace with a broken panel — it is one with
 * nothing to suggest, and four rows of zero would be worse than silence. The
 * contract refuses a `count` below one from the other side, so the empty case
 * cannot arrive as `0` dressed up as an opportunity.
 *
 * A rejection here blanks this card alone. Nothing above it depends on the
 * suggestions, and nothing about them is urgent.
 */
export async function fetchOpportunities(signal?: AbortSignal): Promise<OpportunitiesResponse> {
  return apiRequest({
    path: '/overview/opportunities',
    schema: opportunitiesResponseSchema,
    signal,
  });
}

/**
 * Turn one of those counts into a segment a campaign can be sent to.
 *
 * **The kind is typed as the whole vocabulary, not as the saveable subset, and
 * that is deliberate.** Only three of the five counts are populations of people,
 * and the screen deliberately holds no list of which — it draws the save control
 * on the tiles whose `action` the API sent as a save, exactly as it holds no
 * opinion about `OPPORTUNITY_ORDER`. So a kind that cannot be saved is refused
 * by `saveableOpportunityKindSchema` at the edge and the refusal — *"Only an
 * audience can become a segment"* — lands in the card's failure line, which is
 * where a person can read it. Narrowing here instead would turn the backstop
 * into a button that does nothing.
 *
 * **No name is sent.** The product names a cohort from its kind and the day it
 * was counted, server-side, because the name a person types on the Overview at
 * 09:00 is the one that means nothing a week later, and only the server knows
 * the count's threshold and the workspace's own day. `name` exists on the
 * request shape for a caller that has a person's own words to pass — the segment
 * detail screen's rename is the one that does — and this call sends `{}` so that
 * intent is explicit rather than implied by an absent body.
 *
 * **Nothing here is idempotent by accident.** A second save of an unchanged
 * population answers 200 with the segment already stored and `created: false`;
 * the flag is for the log and the verify suite and is never rendered, because a
 * flag the screen interprets is a sentence waiting to be written.
 */
export async function saveOpportunityCohort(kind: OpportunityKind): Promise<SaveCohortResponse> {
  return apiRequest({
    path: `/overview/opportunities/${encodeURIComponent(kind)}/cohort`,
    method: 'POST',
    body: saveCohortRequestSchema.parse({}),
    schema: saveCohortResponseSchema,
  });
}

/**
 * The dates worth campaigning on, inside the contract's window, for this
 * workspace's country.
 *
 * **Its own request, and the cheapest of the five.** For a workspace with no
 * country set — most of them today — the API answers after a single
 * `SELECT country FROM tenants LIMIT 1` and the card draws nothing. Folded into
 * `GET /overview` that cost would be paid inside the transaction that produces
 * the bounce rate, for a panel that is usually absent.
 *
 * `country: null` is a success, not a failure. It means Wend does not know which
 * country's dates apply to this workspace, which is a different thing from the
 * request going wrong, and the card distinguishes them: nothing drawn for the
 * first, a retry for the second.
 */
export async function fetchCalendar(signal?: AbortSignal): Promise<CalendarResponse> {
  return apiRequest({
    path: '/overview/calendar',
    schema: calendarResponseSchema,
    signal,
  });
}

/**
 * Add a date to this workspace's own calendar, and remove one.
 *
 * The only two writes the Overview makes. Both go through the same
 * `apiRequest` as every read here, so a field error comes back in the shape
 * `apiFieldErrors` already understands and the card needs no error handling of
 * its own.
 *
 * `removeOwnMoment` resolves to nothing rather than to the row it removed:
 * there is no row left to describe, and a caller that wanted one would be a
 * caller keeping a local copy of a list the server owns. After either, the card
 * calls `reload()` — so what is on screen is always what the API answered, and
 * a write that succeeded against a row the next read cannot see is visible
 * rather than papered over.
 */
export async function addOwnMoment(request: AddOwnMomentRequest): Promise<OwnMoment> {
  const response = await apiRequest({
    path: '/overview/own-moments',
    method: 'POST',
    body: addOwnMomentRequestSchema.parse(request),
    schema: ownMomentResponseSchema,
  });
  return response.moment;
}

export async function removeOwnMoment(momentId: string): Promise<void> {
  await apiRequest({
    path: `/overview/own-moments/${momentId}`,
    method: 'DELETE',
    schema: okResponseSchema,
  });
}

/**
 * The reach ledger — who this workspace can reach, per surface.
 *
 * Its own request for the reason the API keeps it on its own route: it is the
 * heaviest read this screen issues and it serves a comparison, not a figure
 * anybody is judged by. A slow answer here leaves every other card untouched.
 */
export async function fetchReach(signal?: AbortSignal): Promise<ReachLedger> {
  return apiRequest({ path: '/overview/reach', schema: reachLedgerSchema, signal });
}
